type StructuredData = {
  '@context': string
  '@type': string
  name: string
  image: string
  url: string
  description: string
  logo?: string
  sameAs?: string[]
  contactPoint?: {
    '@type': string
    telephone: string
    email: string
    contactType: string
    areaServed: string
    availableLanguage: string[]
  }

  provider?: { '@id': string }

  serviceType?: string
  areaServed?: string | object[]
  audience?: {
    '@type': string
    audienceType: string
  }
  category?: string
}

export const DEFAULT_STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  name: 'SpotMe',
  legalName: 'Northern Land Domain S.A.P.I. de C.V.',
  address: {
    streetAddress: 'Av. Manuel Gómez Morín 350-PB 06A',
    addressLocality: 'San Pedro Garza García',
    addressRegion: 'N.L.',
    postalCode: '66265',
    addressCountry: 'MX'
  },
  id: 'https://www.spotme.mx',
  url: 'https://www.spotme.mx',
  logo: 'https://www.spotme.mx/logos/logo.png',
  image: 'https://www.spotme.mx/images/cover.png',
  sameAs: [
    'https://www.facebook.com/spotmemx',
    'https://www.instagram.com/spotme.mx',
    'https://www.youtube.com/@spotme_mx',
    'https://www.tiktok.com/@spotme.mx',
    'https://www.linkedin.com/company/spotmemx',
    'https://maps.app.goo.gl/MrfYDy6mQycctBbc7'
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+52-81-1821-6319',
    email: 'contacto@spotme.mx',
    contactType: 'customer service',
    areaServed: 'MX',
    availableLanguage: ['Spanish', 'English']
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 25.6514,
    longitude: -100.3491
  },
  areaServed: [
    // Major cities
    {
      '@type': 'City',
      name: 'Monterrey',
      sameAs: 'https://en.wikipedia.org/wiki/Monterrey'
    },
    {
      '@type': 'City',
      name: 'Ciudad de México',
      sameAs: 'https://en.wikipedia.org/wiki/Mexico_City'
    },
    {
      '@type': 'City',
      name: 'Guadalajara',
      sameAs: 'https://en.wikipedia.org/wiki/Guadalajara'
    },
    {
      '@type': 'City',
      name: 'Querétaro',
      sameAs: 'https://en.wikipedia.org/wiki/Quer%C3%A9taro'
    },
    {
      '@type': 'City',
      name: 'Puebla',
      sameAs: 'https://en.wikipedia.org/wiki/Puebla'
    },
    {
      '@type': 'City',
      name: 'Tijuana',
      sameAs: 'https://en.wikipedia.org/wiki/Tijuana'
    },
    {
      '@type': 'City',
      name: 'Mérida',
      sameAs: 'https://en.wikipedia.org/wiki/M%C3%A9rida,_Yucat%C3%A1n'
    },
    {
      '@type': 'City',
      name: 'León',
      sameAs: 'https://en.wikipedia.org/wiki/Le%C3%B3n,_Guanajuato'
    },
    {
      '@type': 'City',
      name: 'Chihuahua',
      sameAs: 'https://en.wikipedia.org/wiki/Chihuahua_(city)'
    },
    {
      '@type': 'City',
      name: 'Cancún',
      sameAs: 'https://en.wikipedia.org/wiki/Canc%C3%BAn'
    },
    {
      '@type': 'City',
      name: 'Aguascalientes',
      sameAs: 'https://en.wikipedia.org/wiki/Aguascalientes'
    },
    {
      '@type': 'City',
      name: 'Hermosillo',
      sameAs: 'https://en.wikipedia.org/wiki/Hermosillo'
    },
    {
      '@type': 'City',
      name: 'Toluca',
      sameAs: 'https://en.wikipedia.org/wiki/Toluca'
    },
    {
      '@type': 'City',
      name: 'Puerto Vallarta',
      sameAs: 'https://en.wikipedia.org/wiki/Puerto_Vallarta'
    },
    {
      '@type': 'City',
      name: 'Los Cabos',
      sameAs: 'https://en.wikipedia.org/wiki/Los_Cabos_Municipality'
    },
    // Monterrey metro area
    {
      '@type': 'City',
      name: 'San Pedro Garza García',
      sameAs: 'https://en.wikipedia.org/wiki/San_Pedro_Garza_Garc%C3%ADa'
    },
    {
      '@type': 'City',
      name: 'Apodaca',
      sameAs: 'https://en.wikipedia.org/wiki/Apodaca'
    },
    {
      '@type': 'City',
      name: 'General Escobedo',
      sameAs: 'https://en.wikipedia.org/wiki/General_Escobedo'
    },
    {
      '@type': 'City',
      name: 'Guadalupe, Nuevo León',
      sameAs: 'https://en.wikipedia.org/wiki/Guadalupe,_Nuevo_Le%C3%B3n'
    },
    {
      '@type': 'City',
      name: 'Santa Catarina, Nuevo León',
      sameAs: 'https://en.wikipedia.org/wiki/Santa_Catarina,_Nuevo_Le%C3%B3n'
    },
    {
      '@type': 'City',
      name: 'San Nicolás de los Garza',
      sameAs: 'https://en.wikipedia.org/wiki/San_Nicol%C3%A1s_de_los_Garza'
    },
    // CDMX neighborhoods / delegaciones
    { '@type': 'City', name: 'Polanco' },
    { '@type': 'City', name: 'Condesa' },
    { '@type': 'City', name: 'Roma Norte' },
    { '@type': 'City', name: 'Santa Fe' },
    { '@type': 'City', name: 'Coyoacán' },
    { '@type': 'City', name: 'Del Valle' },
    { '@type': 'City', name: 'Naucalpan' },
    { '@type': 'City', name: 'Tlalnepantla' },
    { '@type': 'City', name: 'Iztapalapa' },
    { '@type': 'City', name: 'Azcapotzalco' },
    // Guadalajara metro area
    {
      '@type': 'City',
      name: 'Zapopan',
      sameAs: 'https://en.wikipedia.org/wiki/Zapopan'
    },
    {
      '@type': 'City',
      name: 'Tlaquepaque',
      sameAs: 'https://en.wikipedia.org/wiki/Tlaquepaque'
    },
    {
      '@type': 'City',
      name: 'Tonalá, Jalisco',
      sameAs: 'https://en.wikipedia.org/wiki/Tonal%C3%A1,_Jalisco'
    },
    // New major cities
    {
      '@type': 'City',
      name: 'Veracruz',
      sameAs: 'https://en.wikipedia.org/wiki/Veracruz_(city)'
    },
    {
      '@type': 'City',
      name: 'Tampico',
      sameAs: 'https://en.wikipedia.org/wiki/Tampico'
    },
    {
      '@type': 'City',
      name: 'Ciudad Madero',
      sameAs: 'https://en.wikipedia.org/wiki/Ciudad_Madero'
    },
    {
      '@type': 'City',
      name: 'Villahermosa',
      sameAs: 'https://en.wikipedia.org/wiki/Villahermosa'
    },
    {
      '@type': 'City',
      name: 'Morelia',
      sameAs: 'https://en.wikipedia.org/wiki/Morelia'
    },
    {
      '@type': 'City',
      name: 'Pachuca',
      sameAs: 'https://en.wikipedia.org/wiki/Pachuca'
    },
    {
      '@type': 'City',
      name: 'Reynosa',
      sameAs: 'https://en.wikipedia.org/wiki/Reynosa'
    },
    {
      '@type': 'City',
      name: 'Mazatlán',
      sameAs: 'https://en.wikipedia.org/wiki/Mazatl%C3%A1n'
    },
    {
      '@type': 'City',
      name: 'Celaya',
      sameAs: 'https://en.wikipedia.org/wiki/Celaya'
    },
    {
      '@type': 'City',
      name: 'Irapuato',
      sameAs: 'https://en.wikipedia.org/wiki/Irapuato'
    },
    {
      '@type': 'City',
      name: 'Silao de la Victoria',
      sameAs: 'https://en.wikipedia.org/wiki/Silao'
    },
    // Torreón metro area
    {
      '@type': 'City',
      name: 'Gómez Palacio',
      sameAs: 'https://en.wikipedia.org/wiki/G%C3%B3mez_Palacio,_Durango'
    },
    {
      '@type': 'City',
      name: 'Lerdo',
      sameAs: 'https://en.wikipedia.org/wiki/Lerdo,_Durango'
    },
    // New CDMX neighborhoods
    { '@type': 'City', name: 'Ecatepec de Morelos' },
    { '@type': 'City', name: 'Cuautitlán Izcalli' },
    { '@type': 'City', name: 'Tultitlán' },
    { '@type': 'City', name: 'Atizapán de Zaragoza' },
    { '@type': 'City', name: 'Texcoco' },
    { '@type': 'City', name: 'Chalco' },
    { '@type': 'City', name: 'Xochimilco' },
    { '@type': 'City', name: 'Tlalpan' },
    { '@type': 'City', name: 'Mixcoac' },
    { '@type': 'City', name: 'Lindavista' }
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00'
    }
  ]
}

export const createStructuredData = (
  required: Pick<StructuredData, '@type' | 'name' | 'description' | 'url'>,
  optional?: Partial<StructuredData>
): StructuredData => {
  return {
    '@context': 'https://schema.org',
    image: DEFAULT_STRUCTURED_DATA.image,
    ...required,
    ...optional
  }
}
